// components/EmployeePointsDialog.jsx
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Switch,
  Grid,
  Paper,
} from "@mui/material";
import { Refresh as RefreshIcon, Circle as CircleIcon } from "@mui/icons-material";
import {
  getEmployeeCompetencyPoints,
  getJobCompetencyPoints,
  syncEmployeePoints,
  addPointsToEmployee,
  removePointsFromEmployee,
  recalculateEmployeeLevel,
} from "services/api";

const PointCard = ({ point, assigned, onToggle, actionLoading, canChangePoints }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: assigned ? (point.type === "WHITE" ? "#bdbdbd" : "#616161") : "#e0e0e0",
        bgcolor: assigned ? (point.type === "WHITE" ? "#fafafa" : "#f5f5f5") : "#ffffff",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: assigned ? (point.type === "WHITE" ? "#9e9e9e" : "#424242") : "#bdbdbd",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box flex={1} mr={1}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {point.title}
          </Typography>

          {point.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                fontSize: "0.8rem",
                lineHeight: 1.4,
              }}
            >
              {point.description}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary">
            Weight: {point.weight}
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" ml={1}>
          <Typography
            variant="caption"
            color={assigned ? "success.main" : "text.disabled"}
            fontWeight={500}
            mb={0.5}
          >
            {assigned ? "Active" : "Inactive"}
          </Typography>
          {canChangePoints && (
            <Switch
              checked={assigned}
              onChange={onToggle}
              disabled={actionLoading}
              size="small"
              color={point.type === "WHITE" ? "default" : "default"}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: point.type === "WHITE" ? "#757575" : "#212121",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: point.type === "WHITE" ? "#bdbdbd" : "#616161",
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const EmployeePointsDialog = ({
  open,
  onClose,
  employeeId,
  compToJobId,
  employeeName,
  competencyName,
  snackbarRef,
  onPointsChanged,
  canChangePoints,
}) => {
  const [loading, setLoading] = useState(false);
  const [employeePoints, setEmployeePoints] = useState([]);
  const [whitePoints, setWhitePoints] = useState([]);
  const [blackPoints, setBlackPoints] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (open && employeeId && compToJobId) {
      fetchPointsData();
    }
  }, [open, employeeId, compToJobId]);

  const fetchPointsData = async () => {
    setLoading(true);
    try {
      const [empPoints, allPoints] = await Promise.all([
        getEmployeeCompetencyPoints(employeeId, compToJobId),
        getJobCompetencyPoints(compToJobId),
      ]);

      if (empPoints !== "error" && allPoints !== "error") {
        setEmployeePoints(empPoints);

        // Separate white and black points
        const white = allPoints.filter((p) => p.type === "WHITE");
        const black = allPoints.filter((p) => p.type === "BLACK");

        setWhitePoints(white);
        setBlackPoints(black);
      } else {
        setEmployeePoints([]);
        setWhitePoints([]);
        setBlackPoints([]);
      }
    } catch (error) {
      snackbarRef.current?.showSnackbar("Failed to load points", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setActionLoading(true);
    const result = await syncEmployeePoints(employeeId, compToJobId);
    if (result !== "error") {
      snackbarRef.current?.showSnackbar("Points synced successfully", "success");
      fetchPointsData();
    } else {
      snackbarRef.current?.showSnackbar("Failed to sync points", "error");
    }
    setActionLoading(false);
  };

  const handleTogglePoint = async (pointId, currentlyAssigned) => {
    setActionLoading(true);

    if (currentlyAssigned) {
      // Remove point
      const result = await removePointsFromEmployee(employeeId, [pointId]);
      if (result !== "error") {
        setEmployeePoints((prev) =>
          prev.filter((ep) => (ep.pointId || ep.compPointId) !== pointId)
        );
        snackbarRef.current?.showSnackbar("Point removed", "success");
        await recalculateEmployeeLevel(employeeId, compToJobId);
        if (onPointsChanged) onPointsChanged();
      } else {
        snackbarRef.current?.showSnackbar("Failed to remove point", "error");
      }
    } else {
      // Add point
      const result = await addPointsToEmployee(employeeId, [pointId]);
      if (result !== "error") {
        // Refresh points to get the new state
        const empPoints = await getEmployeeCompetencyPoints(employeeId, compToJobId);
        if (empPoints !== "error") {
          setEmployeePoints(empPoints);
        }
        snackbarRef.current?.showSnackbar("Point added", "success");
        await recalculateEmployeeLevel(employeeId, compToJobId);
        if (onPointsChanged) onPointsChanged();
      } else {
        snackbarRef.current?.showSnackbar("Failed to add point", "error");
      }
    }

    setActionLoading(false);
  };

  const isPointAssigned = (pointId) => {
    return employeePoints.some((ep) => (ep.pointId || ep.compPointId) === pointId);
  };

  const getWhiteCount = () => {
    return whitePoints.filter((p) => isPointAssigned(p.id)).length;
  };

  const getBlackCount = () => {
    return blackPoints.filter((p) => isPointAssigned(p.id)).length;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6">{employeeName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {competencyName}
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip
                icon={<CircleIcon sx={{ fontSize: 12, color: "#757575 !important" }} />}
                label={`White: ${getWhiteCount()}/${whitePoints.length}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: "#e0e0e0" }}
              />
              <Chip
                icon={<CircleIcon sx={{ fontSize: 12, color: "#212121 !important" }} />}
                label={`Black: ${getBlackCount()}/${blackPoints.length}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: "#e0e0e0" }}
              />
            </Box>
          </Box>
          <Tooltip title="Sync with available points">
            <IconButton onClick={handleSync} disabled={actionLoading} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* White Points Column */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                  border: "1px solid #e0e0e0",
                  height: "100%",
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CircleIcon sx={{ fontSize: 16, color: "#757575" }} />
                  <Typography variant="h6" sx={{ fontSize: "1rem" }}>
                    White Points
                  </Typography>
                  <Chip
                    label={`${getWhiteCount()}/${whitePoints.length}`}
                    size="small"
                    sx={{ ml: "auto" }}
                  />
                </Box>

                {whitePoints.length > 0 ? (
                  whitePoints.map((point) => (
                    <PointCard
                      key={point.id}
                      point={point}
                      assigned={isPointAssigned(point.id)}
                      onToggle={() => handleTogglePoint(point.id, isPointAssigned(point.id))}
                      actionLoading={actionLoading}
                    />
                  ))
                ) : (
                  <Box textAlign="center" py={3}>
                    <Typography variant="body2" color="text.secondary">
                      No white points available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Black Points Column */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#f5f5f5",
                  border: "1px solid #e0e0e0",
                  height: "100%",
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CircleIcon sx={{ fontSize: 16, color: "#212121" }} />
                  <Typography variant="h6" sx={{ fontSize: "1rem" }}>
                    Black Points
                  </Typography>
                  <Chip
                    label={`${getBlackCount()}/${blackPoints.length}`}
                    size="small"
                    sx={{ ml: "auto" }}
                  />
                </Box>

                {blackPoints.length > 0 ? (
                  blackPoints.map((point) => (
                    <PointCard
                      key={point.id}
                      point={point}
                      assigned={isPointAssigned(point.id)}
                      onToggle={() => handleTogglePoint(point.id, isPointAssigned(point.id))}
                      actionLoading={actionLoading}
                    />
                  ))
                ) : (
                  <Box textAlign="center" py={3}>
                    <Typography variant="body2" color="text.secondary">
                      No black points available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Empty State */}
            {whitePoints.length === 0 && blackPoints.length === 0 && (
              <Grid item xs={12}>
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary" gutterBottom>
                    No points available for this competency
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Add points in the Competency to Job page first
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PointCard.propTypes = {
  point: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    weight: PropTypes.number.isRequired,
    type: PropTypes.oneOf(["WHITE", "BLACK"]).isRequired,
  }).isRequired,
  assigned: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  canChangePoints: PropTypes.bool,
};

EmployeePointsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  compToJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  employeeName: PropTypes.string,
  competencyName: PropTypes.string,
  snackbarRef: PropTypes.shape({
    current: PropTypes.shape({
      showSnackbar: PropTypes.func,
    }),
  }).isRequired,
  onPointsChanged: PropTypes.func,
  canChangePoints: PropTypes.bool,
};

EmployeePointsDialog.defaultProps = {
  employeeId: null,
  compToJobId: null,
  employeeName: "",
  competencyName: "",
  onPointsChanged: null,
  canChangePoints: false,
};
export default EmployeePointsDialog;
